/**
 * src/lib/voice/logVoiceQueueEvent.js — TICKET-035 V-8 observability.
 *
 * Plan: docs/plans/2026-05-01-voice-sessions-and-image-sync-plan.md (V-8).
 *
 * Thin fire-and-forget wrapper around logAppError with a stable schema for
 * voice queue lifecycle events. Mirrors logVoiceCaptureFailure (TICKET-020)
 * but for the V-2/V-4 queue layer.
 *
 * Per CLAUDE.md observability rule: every external-provider call must have
 * a *_ok / *_error log with duration_ms; every action must have an
 * intent + result pair. The voice queue's lifecycle events:
 *
 *   - voice_queue:enqueue         — intake created a queued recording
 *   - voice_queue:upload_start    — worker picked the recording, processFn called
 *   - voice_queue:upload_ok       — processFn returned successfully (status=ready)
 *   - voice_queue:upload_error    — processFn threw; worker will retry or abandon
 *   - voice_queue:retry           — worker scheduled a retry (next_retry_at set)
 *   - voice_queue:abandon         — worker hit MAX_RETRIES; status=error terminal
 *
 * SQL query the CEO uses to monitor (per CLAUDE.md):
 *   SELECT error_type, metadata->>'event' AS ev, metadata->>'scope_type' AS scope,
 *          COUNT(*) n, AVG((metadata->>'duration_ms')::int) AS avg_ms
 *   FROM app_error_logs
 *   WHERE error_type = 'voice_queue' AND created_at > now() - INTERVAL '6 hours'
 *   GROUP BY 1, 2, 3 ORDER BY n DESC;
 *
 * Fire-and-forget contract: never throws. Logging failure must never mask
 * the voice failure or queue mutation the caller is in the middle of.
 */
import { logAppError } from '../errorReporter';
import { deviceInfo } from './audioGuards.js';

const VALID_EVENTS = new Set([
  'enqueue',
  'upload_start',
  'upload_ok',
  'upload_error',
  'retry',
  'abandon',
  // CEO ask 2026-05-03 — log file-validation rejections too, so we can spot
  // device/browser-specific patterns (iOS file.type='', Android-Chrome etc.)
  // BEFORE the next user hits them. Fired BEFORE enqueue so recording_id is
  // a placeholder ('pre-enqueue').
  'reject',
  // Night Watch 2026-10-26 — a worker tick rejected (WebKit IndexedDB read
  // failure after backgrounding); the loop logs it and keeps polling.
  'tick_error',
]);

/**
 * @param {Object} params
 * @param {string} params.event           — one of VALID_EVENTS (without 'voice_queue:' prefix)
 * @param {string} params.recording_id    — IDB recording_id
 * @param {string} params.scope_type      — project | task | deal | bundle | general | task_image
 * @param {string} [params.scope_id]
 * @param {number} [params.duration_ms]   — wall-clock for upload_ok / upload_error / retry / abandon
 * @param {number} [params.attempt]       — retry attempt count for retry/abandon
 * @param {string|Error} [params.error]   — error string for upload_error / retry / abandon
 */
export function logVoiceQueueEvent({
  event,
  recording_id,
  scope_type,
  scope_id,
  duration_ms,
  attempt,
  error,
  // TICKET-1079: blob size / mime / permanent-failure flag + device, so an empty
  // or unsupported recording is diagnosable from the log alone.
  bytes,
  mime,
  permanent,
}) {
  try {
    if (!VALID_EVENTS.has(event)) {
      // Don't throw — wrong event name is a programmer bug, but we log it
      // and move on so a typo in a worker callsite doesn't break the queue.
      // eslint-disable-next-line no-console
      console.warn('[logVoiceQueueEvent] unknown event:', event);
      return;
    }

    const isError = error instanceof Error;
    const error_message = error
      ? (isError ? (error.message || '').slice(0, 2000) : String(error).slice(0, 2000))
      : undefined;

    logAppError({
      type: 'voice_queue',
      message: `voice_queue:${event} ${recording_id || '?'} (${scope_type || '?'})`,
      stack: isError ? (error.stack || '').slice(0, 4000) : undefined,
      view: 'voice_queue',
      metadata: {
        event,
        recording_id,
        scope_type,
        scope_id,
        duration_ms,
        attempt,
        error_message,
        ...(bytes != null ? { bytes } : {}),
        ...(mime ? { mime } : {}),
        ...(permanent != null ? { permanent } : {}),
        ...(error ? deviceInfo() : {}),
      },
    });
  } catch {
    // Fire-and-forget — never re-throw.
  }
}
